import React from 'react';
import { Dropdown, Layout, Menu } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { HomeLink } from '../../../components/HomeLink';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { PlanMemberMark } from '../../../components/PlanMemberMark';
import { planMemberTier } from '../pricing/pricingUtils';
import { useTranslation } from '../../../i18n';
import { userInitial } from '../../../utils/userInitial';
import { useCurrentAccount } from './currentAccountContext';

const logo = `${process.env.PUBLIC_URL || ''}/Wowbit-logo-2.png`;

type MainHeaderProps = {
  userDisplayName: string;
  logout: () => void;
};

export function MainHeader({ userDisplayName, logout }: MainHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const planName = account?.membership?.name?.trim() || '—';
  const planTier = planMemberTier({
    displayName: account?.membership?.name,
    grade: account?.membership?.grade
  });
  const avatarLetter = userInitial(userDisplayName);

  const userMenuPanel = (
    <div className="main-header-user-panel">
      <div className="main-header-user-info">
        <span className="main-header-user-avatar" aria-hidden>
          {avatarLetter}
        </span>
        <div className="main-header-user-meta">
          <div className="main-header-user-name">{userDisplayName}</div>
          {/* <div className="main-header-user-role">{userRoleLabel}</div> */}
        </div>
      </div>
      <Menu
        selectable={false}
        className="main-header-user-menu"
        items={[
          {
            key: 'profile',
            label: t('layout.profile'),
            onClick: () => navigate('/creator/user_center'),
          },
          {
            key: 'upgrade',
            label: t('layout.upgrade'),
            onClick: () => navigate('/creator/upgrade'),
          },
          { type: 'divider' },
          {
            key: 'logout',
            label: t('common.logout'),
            danger: true,
            icon: <LogoutOutlined />,
            onClick: logout,
          },
        ]}
      />
    </div>
  );

  const { Header } = Layout;

  return (
    <Header className="main-header">
      <div className="main-header-inner">
        <Link to='/' className="main-header-brand" aria-label={t('common.home')}>
          <img src={logo} alt="Wowbit" className="main-header-brand-logo" />
          <p className="main-header-brand-title">
            <span className="main-header-brand-dot" aria-hidden>
              ·
            </span>
            {t('layout.brand_product')}
          </p>
        </Link>
        <div className="main-header-actions">
          <HomeLink />
          <LanguageSwitcher />
          <span className={`main-header-plan-badge${planTier ? ` main-header-plan-badge--${planTier}` : ''}`}>
            <PlanMemberMark
              name={account?.membership?.name}
              grade={account?.membership?.grade}
              className="plan-member-mark plan-member-mark--badge"
            />
            {planName}
          </span>
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            arrow={false}
            overlayClassName="main-header-user-overlay"
            dropdownRender={() => userMenuPanel}
          >
            <button type="button" className="main-header-avatar" aria-label={t('layout.user_menu')}>
              {avatarLetter}
            </button>
          </Dropdown>
        </div>
      </div>
    </Header>
  );
}
